/**
 * History (roadmap D240): the project's runs on a time axis, one row each, and below it the
 * activity graph of the run picked: the same picture, filters, time scrubber, search and export
 * as AI Workbench's History, drawn by the files mirrored in `./activityGraph/` (MIRROR.md).
 *
 * The address is `/projects/:id?tab=overview&part=history&run=<runId>`; `run` picks the run whose
 * graph shows. The graph's scope is the run, or the run's agent over 30 days, its task or its
 * conversation, whichever the run has.
 */

import type { ActivityGraph, ActivityNode, HistoryRun, HistoryRunStatus } from '@aioe/shared';
import { dateTime } from '@aioe/shared/wording/dateTime';
import { ArrowUpRightFromSquare, Clock, ClockArrowRotateLeft } from '@gravity-ui/icons';
import { Button, Modal, Popover } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { type MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { routes } from '../api/client.ts';
import { timeAgo, useLoad, useNow } from '../hooks.ts';
import { useMoney } from '../money.tsx';
import { ActivityGraphControls, ActivityGraphDetail, ActivityGraphPicture } from './activityGraph/ActivityGraph.tsx';
import { ALL_GROUPS, exportName, exportSvg, FILTER_GROUPS, type FilterGroupId } from './activityGraph/activityGraphModel.ts';
import { useActivityGraphView } from './activityGraph/useActivityGraphView.ts';
import { duration } from './projectRuns.tsx';
import { ChoiceSelect, ErrorNote, ListBlock, ListGroup, SectionHeading, Stat, StatusMark } from './ui.tsx';
import { ArtEmpty, VIEW_TONES } from './ui/Art.tsx';
import { SharedWithYou } from './sharedConversations.tsx';
import { LiveSessionsSection } from './live/LiveSessions.tsx';
import { CopyRunIdButton } from './ui/RunId.tsx';
import { TimelinePanel } from './timeline/TimelinePanel.tsx';
import { TimelineOpenProvider } from './timeline/timelineOpen.tsx';
import './projectHistory.css';

type Window = 'day' | 'week' | 'month';
const WINDOWS: Record<Window, { label: string; ms: number }> = {
  day: { label: 'Last 24 hours', ms: 86_400_000 },
  week: { label: 'Last 7 days', ms: 7 * 86_400_000 },
  month: { label: 'Last 30 days', ms: 30 * 86_400_000 },
};

type Scope = 'run' | 'agent' | 'task' | 'conversation';
const SCOPE_TEXT: Record<Scope, string> = { run: 'This run', agent: 'Agent, 30 days', task: 'Task', conversation: 'Conversation' };

/** The status as a dot and a word: the words Team Work's history uses. */
const STATUS: Record<HistoryRunStatus, { state: 'connected' | 'working' | 'idle' | 'error'; label: string }> = {
  running: { state: 'working', label: 'Running' },
  ok: { state: 'connected', label: 'Finished' },
  error: { state: 'error', label: 'Failed' },
  cancelled: { state: 'idle', label: 'Cancelled' },
};

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const fmt = (iso: string) => dateTime(iso, { date: 'medium', year: false });

export interface HistoryRoot {
  rootId: string;
  deviceId?: string;
}

export function ProjectHistory({ projectId, roots, runId, onRun }: { projectId: string; roots: HistoryRoot[]; runId?: string; onRun: (id: string | undefined) => void }) {
  const tone = VIEW_TONES.projects!;
  const now = useNow();
  const { format } = useMoney();
  const [win, setWin] = useState<Window>(runId ? 'month' : 'week'); // a link to one run looks as far back as the graph does
  // To the minute, so the query changes no more often than the clock does.
  const from = new Date(Math.floor((now - WINDOWS[win].ms) / 60_000) * 60_000).toISOString();
  const data = useLoad(() => routes.historyRuns(projectId, { from, limit: 300 }), [projectId, from], 30_000);
  const runs = useMemo(() => [...(data.data?.runs ?? [])].sort((a, b) => b.startedAt.localeCompare(a.startedAt)), [data.data]);
  const picked = runId ? runs.find((r) => r.id === runId) : undefined;
  // D282: sessions in this project open to remote control that this person may watch (or, their own, control).
  const live = useLoad(() => routes.projectLive(projectId).catch(() => undefined), [projectId], 30_000);

  const totals = useMemo(() => {
    let tools = 0;
    let files = 0;
    let cost = 0;
    let running = 0;
    for (const r of runs) {
      tools += r.toolCalls;
      files += r.filesChanged ?? 0;
      cost += r.costUsd ?? 0;
      if (r.status === 'running') running++;
    }
    return { tools, files, cost, running };
  }, [runs]);

  return (
    <>
      <div className="art-stats">
        <Stat tone={tone} icon={<Clock />} label="Runs" value={data.data ? runs.length : undefined} detail={totals.running ? `${totals.running} running now` : WINDOWS[win].label.toLowerCase()} />
        <Stat tone={tone} icon={<Clock />} label="Tools called" value={data.data ? totals.tools : undefined} detail="Across these runs" />
        <Stat tone={tone} icon={<Clock />} label="Files changed" value={data.data ? totals.files : undefined} detail="Where the run reports them" />
        <Stat tone={tone} icon={<Clock />} label="Cost" value={data.data ? (totals.cost ? format(totals.cost, { compact: true }) : '—') : undefined} detail={totals.cost ? 'Recorded by the runs' : 'No cost recorded'} />
      </div>
      <ErrorNote error={data.error} />
      <LiveSessionsSection tone={tone} title="Live" hint="Team Work and conversations shared with the project, as they happen." rows={live.data?.sessions} project={projectId} />
      {/* The workbench's work timeline (./timeline/, ported): one lane per agent, zoom, the scrubber and the filters; a run's details offer its activity graph below. */}
      {/* The timeline brings its own surface, so it stands under its heading with no card around it. */}
      <section className="list-group-section" aria-label="Work timeline">
        <SectionHeading title="Work timeline" description="One lane per agent. Ctrl and the wheel zoom; drag the strip under the lanes to move along; pick a bar for what it was." />
        <TimelineOpenProvider value={{ projectId, onRun, ...(runId ? { shownRun: runId } : {}) }}>
          <TimelinePanel scope="project" projectId={projectId} lanesToggle chips hideStats commits rangeView="project-history" />
        </TimelineOpenProvider>
      </section>
      <ListGroup
        title="Runs"
        count={data.data ? runs.length : undefined}
        description={data.data?.truncated ? 'The newest 300 are shown; shorten the window to see everything.' : 'Newest first; pick a bar for its summary.'}
        actions={<ChoiceSelect label="Window" value={win} options={Object.fromEntries((Object.keys(WINDOWS) as Window[]).map((k) => [k, WINDOWS[k].label])) as Record<Window, string>} onChange={setWin} />}
        empty={
          data.data && runs.length === 0 ? (
            <ArtEmpty tone={tone} icon={<ClockArrowRotateLeft />} compact title="History arrives as workbenches upload it">
              Each stretch of an agent's work is recorded by the workbench it ran on and shows here, with what it did, as the workbench uploads it.
            </ArtEmpty>
          ) : undefined
        }
      >
        {runs.length === 0 ? (
          <ListBlock className="text-sm text-muted">Reading the runs…</ListBlock>
        ) : (
          <ListBlock flush>
            <Timeline runs={runs} now={now} selected={runId} onPick={onRun} roots={roots} />
          </ListBlock>
        )}
      </ListGroup>
      <SharedWithYou projectId={projectId} />
      <ActivitySection projectId={projectId} runId={runId} run={picked} loaded={Boolean(data.data)} windowFrom={from} />
    </>
  );
}

/** One row per run: who, a bar from its start to its end on a shared axis, and its status as a dot and a word. */
function Timeline({ runs, now, selected, onPick, roots }: { runs: HistoryRun[]; now: number; selected?: string; onPick: (id: string) => void; roots: HistoryRoot[] }) {
  // Never called with no runs (the panel shows the empty state), and a run with an unreadable time counts from now.
  const starts = runs.map((r) => Date.parse(r.startedAt)).filter(Number.isFinite);
  const start = starts.length ? Math.min(...starts) : now;
  const end = Math.max(now, ...runs.map((r) => (r.endedAt ? Date.parse(r.endedAt) : now)).filter(Number.isFinite));
  const span = Math.max(end - start, 1);
  const place = (r: HistoryRun) => {
    const a = (Date.parse(r.startedAt) - start) / span;
    const b = ((r.endedAt ? Date.parse(r.endedAt) : now) - start) / span;
    return { left: `${(a * 100).toFixed(2)}%`, width: `${Math.max((b - a) * 100, 0.8).toFixed(2)}%` };
  };
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 border-b border-separator px-4 py-2 text-xs text-muted tabular-nums">
        <span>{fmt(new Date(start).toISOString())}</span>
        <span>Now</span>
      </div>
      <ul className="divide-y divide-separator">
        {runs.map((r) => (
          <li key={r.id} className="grid gap-x-4 gap-y-1 px-4 py-2 text-sm sm:grid-cols-[minmax(9rem,14rem)_minmax(0,1fr)_8.5rem] sm:items-center" data-selected={r.id === selected || undefined}>
            <div className="min-w-0">
              <div className="truncate">
                <span className="font-medium">{r.agent}</span>
                <span className="text-muted"> · {r.teamName ?? r.teamId}</span>
              </div>
              {r.title && <div className="truncate text-xs text-muted">{r.title}</div>}
              {r.fallbackTo && <div className="text-xs text-muted">Ran on the backup model</div>}
            </div>
            <div className="run-track">
              <RunBar run={r} now={now} style={place(r)} selected={r.id === selected} onPick={onPick} roots={roots} />
            </div>
            <div className="flex items-center justify-between gap-3 sm:justify-end">
              <StatusMark state={STATUS[r.status].state} label={STATUS[r.status].label} />
              <CopyRunIdButton runId={r.id} />
              <span className="text-xs tabular-nums text-muted sm:hidden">{duration(runMs(r, now))}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Why a turn left its model for the backup (workbench D312). */
const FALLBACK_REASON: Record<NonNullable<HistoryRun['fallbackTo']>['reason'], string> = { 'rate-limited': 'rate-limited', overloaded: 'overloaded', dropped: 'dropped mid-turn', refused: 'refused' };

const runMs = (r: HistoryRun, now: number) => Math.max(0, (r.endedAt ? Date.parse(r.endedAt) : now) - Date.parse(r.startedAt));

/** The bar, and the popover it opens: the run's summary and the two things to do with it. */
function RunBar({ run, now, style, selected, onPick, roots }: { run: HistoryRun; now: number; style: { left: string; width: string }; selected: boolean; onPick: (id: string) => void; roots: HistoryRoot[] }) {
  const { format } = useMoney();
  const device = roots.find((x) => x.rootId === run.rootId)?.deviceId;
  const rows: Array<[string, string]> = [
    ['Agent', `${run.agent} · ${run.teamName ?? run.teamId}`],
    ['Started', fmt(run.startedAt)],
    ['Ended', run.endedAt ? fmt(run.endedAt) : 'Still going'],
    ['Took', duration(runMs(run, now))],
    ['Did', [plural(run.toolCalls, 'tool call'), run.filesChanged !== undefined ? plural(run.filesChanged, 'file') + ' changed' : undefined, plural(run.questions, 'question')].filter(Boolean).join(' · ')],
  ];
  if (run.costUsd) rows.push(['Cost', format(run.costUsd, { compact: true })]);
  if (run.fallbackTo) rows.push(['Ran on', `the backup, ${run.fallbackTo.provider}/${run.fallbackTo.model}: the first model was ${FALLBACK_REASON[run.fallbackTo.reason]}`]);
  return (
    <Popover>
      <Button variant="ghost" size="sm" className="run-bar" style={style} data-state={run.status} aria-pressed={selected} aria-label={`${run.agent}, ${STATUS[run.status].label.toLowerCase()}, started ${timeAgo(run.startedAt)}`} />
      <Popover.Content placement="bottom" className="w-80 max-w-[calc(100vw-1rem)]">
        <Popover.Dialog className="grid gap-3 p-3" aria-label={`Run by ${run.agent}`}>
          <div className="flex items-center justify-between gap-3">
            <StatusMark state={STATUS[run.status].state} label={STATUS[run.status].label} />
            <span className="text-xs text-muted">{timeAgo(run.startedAt)}</span>
          </div>
          {run.title && <p className="m-0 line-clamp-3 text-sm">{run.title}</p>}
          <dl className="m-0 grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
            {rows.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-muted">{k}</dt>
                <dd className="m-0 min-w-0 break-words tabular-nums">{v}</dd>
              </div>
            ))}
          </dl>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant={selected ? 'secondary' : 'primary'} onPress={() => onPick(run.id)}>
              {selected ? 'Showing its activity' : 'Show its activity'}
            </Button>
            {device && (
              <Link to={`/workbenches/${encodeURIComponent(device)}?view=team-work`} className="inline-flex items-center gap-1 text-sm text-accent hover:underline">
                Open in Team Work <ArrowUpRightFromSquare className="size-3.5" aria-hidden />
              </Link>
            )}
          </div>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

/** What the scope switch offers for a run, and the query each answers with. */
function scopesOf(run: HistoryRun | undefined, runId: string | undefined): Record<Scope, { id: string; team?: string } | undefined> {
  return {
    run: runId ? { id: runId } : undefined,
    agent: run ? { id: run.agent, team: run.teamId } : undefined,
    task: run?.task ? { id: run.task.id, team: run.task.board } : undefined,
    conversation: run?.conversationId ? { id: run.conversationId } : undefined,
  };
}

/** What the part shows right now, carried into the full-screen graph as its starting filters and range. */
interface GraphCarry {
  groups: ReadonlySet<FilterGroupId>;
  query: string;
  /** The scrubber's window, in ms, when it was narrowed. */
  window?: { from: number; to: number };
}

/** The node kinds a set of filter groups stands for; the run and its agents are always drawn, so always asked for. */
function kindsOf(groups: ReadonlySet<FilterGroupId>): string | undefined {
  if (groups.size === ALL_GROUPS.size) return undefined;
  return ['run', 'agent', ...FILTER_GROUPS.filter((g) => groups.has(g.id)).flatMap((g) => g.kinds)].join(',');
}

function ActivitySection({ projectId, runId, run, loaded, windowFrom }: { projectId: string; runId?: string; run?: HistoryRun; loaded: boolean; windowFrom: string }) {
  const tone = VIEW_TONES.projects!;
  const [scope, setScope] = useState<Scope>('run');
  // "See this agent over 30 days" from a node names an agent the run itself did not.
  const [agent, setAgent] = useState<string | undefined>();
  const [explore, setExplore] = useState<GraphCarry | undefined>();
  const carry = useRef<GraphCarry | undefined>(undefined);
  const options = scopesOf(run, runId);
  const active: Scope = options[scope] || agent ? scope : 'run';
  const query = agent && active === 'agent' ? { id: agent } : options[active];
  const graph = useLoad(
    () => (runId && query ? routes.historyGraph(projectId, { scope: active, id: query.id, ...('team' in query && query.team ? { team: query.team } : {}) }) : Promise.resolve(undefined)),
    [projectId, runId, active, query?.id, query && 'team' in query ? query.team : undefined],
  );
  const available = (Object.keys(SCOPE_TEXT) as Scope[]).filter((s) => s === active || options[s]);

  return (
    <section className="list-group-section" aria-label="Activity">
      <SectionHeading
        title="Activity"
        description={graph.data?.title ?? (runId ? undefined : 'The agents, tools, files and questions of a run.')}
        actions={
          runId && (graph.data || available.length > 1) ? (
            <>
              {available.length > 1 && (
                <Segment aria-label="What the graph covers" size="sm" selectedKey={active} onSelectionChange={(k) => (setAgent(undefined), setScope(String(k) as Scope))} className="w-fit">
                  {available.map((s) => (
                    <Segment.Item key={s} id={s}>
                      {SCOPE_TEXT[s]}
                    </Segment.Item>
                  ))}
                </Segment>
              )}
              {graph.data && graph.data.nodes.length > 0 && (
                <Button size="sm" variant="secondary" onPress={() => setExplore(carry.current ?? { groups: ALL_GROUPS, query: '' })}>
                  Explore
                </Button>
              )}
            </>
          ) : undefined
        }
      />
      {!runId ? (
        <ArtEmpty tone={tone} icon={<ClockArrowRotateLeft />} compact title="Pick a run">
          Open a run on the timeline and choose Show its activity to see what it did.
        </ArtEmpty>
      ) : (
        <>
          <ErrorNote error={graph.error} />
          {graph.data ? (
            <GraphStage key={`${graph.data.at}/${active}/${query?.id}`} graph={graph.data} scope={active} onAgent={(name) => (setAgent(name), setScope('agent'))} carryRef={carry} />
          ) : (
            <div className="ag-stage">
              <p className="ag-stage__note m-0" role="status">
                {graph.error ? 'The graph could not be read.' : loaded && !run && !graph.loading ? 'That run is not in this project, or is older than 30 days.' : 'Reading the history…'}
              </p>
            </div>
          )}
        </>
      )}
      {explore && query && (
        <ExploreGraph
          projectId={projectId}
          scope={active}
          query={query}
          // An agent's graph covers 30 days; the Runs window is the part's own time window, so it is the one carried. A run, task or conversation is its own span.
          from={active === 'agent' ? windowFrom : undefined}
          carry={explore}
          onAgent={(name) => (setExplore(undefined), setAgent(name), setScope('agent'))}
          onClose={() => setExplore(undefined)}
        />
      )}
    </section>
  );
}

/**
 * The full-screen graph: the same scope the part shows, asked of the API with the part's node-type
 * filters (`types`), search (`search`) and time window (`from`), drawn by the same renderer, which
 * starts with the same filters, search and scrubber window. Close returns to the part untouched.
 */
function ExploreGraph({ projectId, scope, query, from, carry, onAgent, onClose }: { projectId: string; scope: Scope; query: { id: string; team?: string }; from?: string; carry: GraphCarry; onAgent: (agent: string) => void; onClose: () => void }) {
  const types = kindsOf(carry.groups);
  const search = carry.query.trim() || undefined;
  const graph = useLoad(
    () => routes.historyGraph(projectId, { scope, id: query.id, ...(query.team ? { team: query.team } : {}), ...(from ? { from } : {}), ...(types ? { types } : {}), ...(search ? { search } : {}) }),
    [projectId, scope, query.id, query.team, from, types, search],
  );
  return (
    <Modal isOpen onOpenChange={(open) => !open && onClose()}>
      <Modal.Backdrop>
        <Modal.Container placement="center" size="full">
          <Modal.Dialog aria-label={graph.data ? `Explore: ${graph.data.title}` : 'Explore the activity graph'} className="explore-graph">
            <Modal.Header className="flex-row items-center justify-between gap-3">
              <Modal.Heading className="min-w-0 truncate">{graph.data?.title ?? 'Explore'}</Modal.Heading>
              <Button size="sm" variant="secondary" onPress={onClose}>
                Close
              </Button>
            </Modal.Header>
            <Modal.Body className="min-h-0 p-0">
              <ErrorNote error={graph.error} />
              {graph.data ? (
                <GraphStage key={graph.data.at} graph={graph.data} scope={scope} onAgent={onAgent} initial={carry} full />
              ) : (
                <div className="ag-stage ag-stage--full">
                  <p className="ag-stage__note m-0" role="status">
                    {graph.error ? 'The graph could not be read.' : 'Reading the history…'}
                  </p>
                </div>
              )}
            </Modal.Body>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}

/** The SVG drawn onto a canvas at twice its size, as a PNG (as the workbench does it). */
async function svgToPngBlob(svg: string, width: number, height: number): Promise<Blob> {
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * 2);
    canvas.height = Math.round(height * 2);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('This window cannot draw on a canvas.');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('The picture could not be drawn as an image.');
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function download(name: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The picture on its dark stage with the controls and a node's detail beside it. */
function GraphStage({ graph: g, scope, onAgent, carryRef, initial, full }: { graph: ActivityGraph; scope: Scope; onAgent: (agent: string) => void; carryRef?: MutableRefObject<GraphCarry | undefined>; initial?: GraphCarry; full?: boolean }) {
  const view = useActivityGraphView(g);
  // The renderer's state starts from its own defaults and has setters only, so a carried filter, search and window go in once, on mount.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !initial) return;
    seeded.current = true;
    for (const grp of FILTER_GROUPS) if (!initial.groups.has(grp.id)) view.toggleGroup(grp.id);
    if (initial.query) view.setQuery(initial.query);
    if (initial.window) {
      const len = view.span.to - view.span.from;
      const clamp = (ms: number) => Math.min(1, Math.max(0, (ms - view.span.from) / len));
      view.setRange([clamp(initial.window.from), clamp(initial.window.to)]);
    }
  }, [initial, view]);
  // What the part shows, kept where the Explore control can read it.
  const { groups, query, range, window: scrubbed } = view;
  useEffect(() => {
    if (carryRef) carryRef.current = { groups, query, ...(scrubbed ? { window: scrubbed } : {}) };
    // `range` stands for `scrubbed`, which is a new object on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carryRef, groups, query, range]);
  const [exporting, setExporting] = useState(false);
  const [note, setNote] = useState<string | undefined>();
  const { layout, graph } = view;

  const onExport = async (format: 'png' | 'svg') => {
    setExporting(true);
    setNote(undefined);
    try {
      const subtitle = `${dateTime(view.window?.from ?? view.span.from)} – ${dateTime(view.window?.to ?? view.span.to)}`;
      const svg = exportSvg(graph, layout, { theme: 'dark', ...(view.window ? { window: view.window } : {}), subtitle });
      const size = /width="(\d+)" height="(\d+)"/.exec(svg);
      const blob = format === 'svg' ? new Blob([svg], { type: 'image/svg+xml' }) : await svgToPngBlob(svg, Number(size?.[1] ?? layout.width), Number(size?.[2] ?? layout.height));
      download(exportName(graph, format), blob);
      setNote(`Saved as ${format.toUpperCase()}.`);
    } catch (err) {
      setNote(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  };

  const links = (n: ActivityNode) =>
    n.kind === 'agent' && n.agent && scope !== 'agent' ? (
      <button type="button" className="stage-link" onClick={() => onAgent(n.agent!)}>
        See {n.agent} over 30 days
      </button>
    ) : undefined;

  if (layout.nodes.length === 0) {
    return (
      <div className="ag-stage">
        <p className="ag-stage__note m-0">Nothing was recorded for this yet. The workbench uploads its history as it goes.</p>
      </div>
    );
  }
  return (
    <div className={full ? 'ag-stage ag-stage--full' : 'ag-stage'}>
      <div className="ag-stage__surface" role="region" aria-label="The activity graph; scroll to see all of it" tabIndex={-1}>
        <ActivityGraphPicture view={view} />
      </div>
      <div className="ag-stage__side">
        <ActivityGraphControls view={view} onExport={(f) => void onExport(f)} exporting={exporting} note={note ? <p className="m-0" role="status">{note}</p> : undefined} />
        {view.selected ? (
          <div className="stage-panel">
            <ActivityGraphDetail node={view.selected} onClose={() => view.select(undefined)} links={links(view.selected)} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
